import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  coach: {
    id: string;
    name: string;
    specialty: string;
    hourlyRate: number;
    avatar?: string;
  };
}

export default function BookingModal({ isOpen, onClose, coach }: BookingModalProps) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [bookingData, setBookingData] = useState({
    date: '',
    time: '',
    duration: 60,
    notes: ''
  });

  const { user, session } = useAuth();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBookingData(prev => ({
      ...prev,
      [name]: name === 'duration' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !session) {
      setMessage('Vous devez être connecté pour réserver');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const sessionDateTime = new Date(`${bookingData.date}T${bookingData.time}`);
      const totalPrice = (coach.hourlyRate * bookingData.duration) / 60;
      
      // Créer la réservation
      const bookingResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          coachId: coach.id,
          sessionDate: sessionDateTime.toISOString(),
          duration: bookingData.duration,
          notes: bookingData.notes
        })
      });

      const bookingResult = await bookingResponse.json();
      
      if (!bookingResponse.ok) {
        setMessage(bookingResult.error || 'Erreur lors de la réservation');
        setLoading(false);
        return;
      }

      // Créer la session de paiement Stripe avec répartition 80/20
      const paymentResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-stripe-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          bookingId: bookingResult.booking.id,
          coachId: coach.id,
          sessionDate: sessionDateTime.toISOString(),
          duration: bookingData.duration,
          totalPrice: totalPrice
        })
      });

      const paymentResult = await paymentResponse.json();
      
      if (!paymentResponse.ok) {
        if (paymentResult.requiresStripeConnect) {
          setMessage('Le coach n\'a pas encore configuré son compte de paiement. Veuillez réessayer plus tard.');
        } else {
          setMessage(paymentResult.error || 'Erreur lors de la création du paiement');
        }
        setLoading(false);
        return;
      }

      // Rediriger vers Stripe Checkout
      window.location.href = paymentResult.url;
      
    } catch (error) {
      setMessage('Une erreur est survenue lors de la réservation');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const totalPrice = (coach.hourlyRate * bookingData.duration) / 60;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Réserver une séance</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Coach Info */}
          <div className="flex items-center mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              {coach.avatar ? (
                <img src={coach.avatar} alt={coach.name} className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <i className="ri-user-line text-xl text-blue-600"></i>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{coach.name}</h3>
              <p className="text-sm text-gray-600">{coach.specialty}</p>
              <p className="text-sm font-medium text-blue-600">{coach.hourlyRate}€/heure</p>
            </div>
          </div>

          {/* Message d'état */}
          {message && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              message.includes('succès') 
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-red-100 text-red-700 border border-red-200'
            }`}>
              {message}
            </div>
          )}

          {!user && (
            <div className="mb-4 p-3 rounded-lg bg-yellow-100 text-yellow-700 border border-yellow-200 text-sm">
              Vous devez être connecté pour réserver. 
              <a href="/connexion" className="ml-1 text-yellow-800 font-medium hover:underline">
                Se connecter
              </a>
            </div>
          )}

          {/* Booking Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={bookingData.date}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">
                  Heure *
                </label>
                <input
                  type="time"
                  id="time"
                  name="time"
                  value={bookingData.time}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-2">
                Durée
              </label>
              <select
                id="duration"
                name="duration"
                value={bookingData.duration}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
              >
                <option value={60}>1 heure</option>
                <option value={90}>1h30</option>
                <option value={120}>2 heures</option>
              </select>
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Notes (optionnel)
              </label>
              <textarea
                id="notes"
                name="notes"
                value={bookingData.notes}
                onChange={handleInputChange}
                rows={3}
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Objectifs, préférences, informations importantes..."
              />
            </div>

            {/* Prix total */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-gray-900">Prix total :</span>
                <span className="text-xl font-bold text-blue-600">{totalPrice.toFixed(2)}€</span>
              </div>
              <p className="text-sm text-gray-600">
                {bookingData.duration} min × {coach.hourlyRate}€/h
              </p>
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs text-gray-600">
                  <i className="ri-information-line mr-1"></i>
                  Le coach reçoit 80% ({(totalPrice * 0.8).toFixed(2)}€), la plateforme 20% ({(totalPrice * 0.2).toFixed(2)}€)
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading || !user}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Réservation...' : 'Réserver'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}